import { useQuery } from "@tanstack/react-query";
import { Pencil, Send, ShieldCheck, UserMinus } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  ErrorState,
  InlineAlert,
  LoadingState,
  StatusBadge,
  cn,
  type Tone,
} from "../../shared/ui";
import type {
  EmployeeDetail,
  MembershipHistoryEntry,
  ProjectHistoryEntry,
} from "../../shared/types/api";
import { AssignRolesDrawer } from "./AssignRolesDrawer";
import { EmployeeEditDrawer } from "./EmployeeEditDrawer";
import { EmploymentStatusDrawer } from "./EmploymentStatusDrawer";

const ACCOUNT_TONE: Record<string, Tone> = {
  ACTIVE: "green",
  INVITED: "blue",
  LOCKED: "amber",
  DISABLED: "gray",
};

/**
 * Employee detail.
 *
 * <p>The team history tab is the §46 guarantee made visible to a person rather than a test: every
 * team someone has been in, with the dates, including periods that have ended. That is the view a
 * manager needs when asking who was working on what last quarter.
 */
export function EmployeeDetailDrawer({
  employeeId,
  onClose,
  onChanged,
}: {
  employeeId: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [tab, setTab] = useState<"details" | "history" | "projects">("details");
  const [notice, setNotice] = useState<string | null>(null);
  // Which secondary panel is open over this one. Each is its own drawer because each carries a
  // different permission and a different consequence.
  const [editing, setEditing] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [assigningRoles, setAssigningRoles] = useState(false);

  const employee = useQuery({
    queryKey: ["employee", employeeId],
    queryFn: () => api.get<EmployeeDetail>(`/api/v1/employees/${employeeId}`),
    enabled: Boolean(employeeId),
  });

  /*
   * §36: somebody is routinely on more than one project at once, and the question a manager asks
   * is "what has this person been working on" — which the team history cannot answer, because a
   * team and a project are different groupings.
   */
  const projects = useQuery({
    queryKey: ["employee-project-history", employeeId],
    queryFn: () =>
      api.get<ProjectHistoryEntry[]>(`/api/v1/employees/${employeeId}/project-history`),
    enabled: Boolean(employeeId) && tab === "projects",
  });

  const history = useQuery({
    queryKey: ["employee-team-history", employeeId],
    queryFn: () =>
      api.get<MembershipHistoryEntry[]>(`/api/v1/employees/${employeeId}/team-history`),
    enabled: Boolean(employeeId) && tab === "history",
  });

  const afterChange = () => {
    void employee.refetch();
    onChanged();
  };

  const reinvite = async () => {
    setNotice(null);
    try {
      await api.post(`/api/v1/employees/${employeeId}/reinvite`);
      setNotice("A new invitation has been sent. Any earlier link for this person no longer works.");
      onChanged();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.message : "Could not send the invitation.");
    }
  };

  return (
    <Drawer
      open={Boolean(employeeId)}
      onClose={() => {
        setTab("details");
        setNotice(null);
        onClose();
      }}
      title={employee.data?.fullName ?? "Employee"}
      subtitle={employee.data ? `${employee.data.designation} · ${employee.data.department}` : undefined}
      width="lg"
    >
      {employee.isPending && <LoadingState />}
      {employee.isError && (
        <ErrorState error={employee.error} onRetry={() => void employee.refetch()} />
      )}

      {employee.isSuccess && (
        <div className="space-y-5">
          <div className="flex gap-1 border-b border-ink-100">
            {(
              [
                ["details", "Details"],
                ["history", "Team history"],
                ["projects", "Project history"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={cn(
                  "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                  tab === key
                    ? "border-brand-600 text-brand-700"
                    : "border-transparent text-ink-500 hover:text-ink-800",
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {notice && <InlineAlert tone="info">{notice}</InlineAlert>}

          {tab === "details" && (
            <div className="space-y-5">
              <dl className="grid grid-cols-[150px_1fr] gap-x-4 gap-y-2.5 text-sm">
                <Row label="Employee code" mono>
                  {employee.data.employeeCode}
                </Row>
                <Row label="Email">{employee.data.officialEmail}</Row>
                <Row label="Phone">{employee.data.phone ?? "—"}</Row>
                <Row label="Manager">{employee.data.reportingManagerName ?? "—"}</Row>
                <Row label="Joined" mono>
                  {employee.data.dateOfJoining}
                </Row>
                {employee.data.dateOfExit && (
                  <Row label="Left" mono>
                    {employee.data.dateOfExit}
                  </Row>
                )}
                <Row label="Working calendar">{employee.data.workingCalendarName}</Row>
                <Row label="Time zone">{employee.data.timeZone}</Row>
              </dl>

              {can("USER_MANAGE") && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Pencil className="h-3.5 w-3.5" />}
                    onClick={() => setEditing(true)}
                  >
                    Edit details
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<UserMinus className="h-3.5 w-3.5" />}
                    onClick={() => setChangingStatus(true)}
                  >
                    Employment status
                  </Button>
                  {can("ROLE_MANAGE") && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<ShieldCheck className="h-3.5 w-3.5" />}
                      onClick={() => setAssigningRoles(true)}
                    >
                      Change roles
                    </Button>
                  )}
                </div>
              )}

              <section className="border-t border-ink-100 pt-4">
                <h3 className="mb-2 text-sm font-semibold text-ink-900">Roles</h3>
                {employee.data.roles.length === 0 ? (
                  <p className="text-sm text-ink-500">
                    No roles assigned, so this person can sign in but see very little.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {employee.data.roles.map((role) => (
                      <StatusBadge key={role} tone="blue">
                        {role.replace(/_/g, " ")}
                      </StatusBadge>
                    ))}
                  </div>
                )}
              </section>

              <section className="border-t border-ink-100 pt-4">
                <h3 className="mb-2 text-sm font-semibold text-ink-900">Account</h3>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={ACCOUNT_TONE[employee.data.accountStatus ?? ""] ?? "gray"}>
                    {employee.data.accountStatus ?? "NO ACCOUNT"}
                  </StatusBadge>
                  {can("USER_MANAGE") && employee.data.accountStatus === "INVITED" && (
                    <Button
                      variant="subtle"
                      size="sm"
                      icon={<Send className="h-3.5 w-3.5" />}
                      onClick={() => void reinvite()}
                    >
                      Resend invitation
                    </Button>
                  )}
                </div>
                {employee.data.accountStatus === "INVITED" && (
                  <p className="mt-1.5 text-sm text-ink-500">
                    They have been invited but have not chosen a password yet.
                  </p>
                )}
              </section>
            </div>
          )}

          {tab === "projects" && (
            <div>
              {projects.isPending && <LoadingState label="Loading project history…" />}
              {projects.isError && (
                <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
              )}
              {projects.isSuccess && projects.data.length === 0 && (
                <p className="text-sm text-ink-500">
                  This person has not been on a project yet.
                </p>
              )}
              {projects.isSuccess && projects.data.length > 0 && (
                <div className="space-y-2">
                  <p className="mb-3 text-sm text-ink-500">
                    Every project period, closed ones included. Somebody is often on more than one
                    at a time, so these overlap.
                  </p>
                  {projects.data.map((entry) => (
                    <div
                      key={entry.membershipId}
                      className={cn(
                        "rounded-lg border border-ink-200 border-l-[3px] bg-surface px-4 py-3",
                        entry.current ? "border-l-brand-500" : "border-l-ink-200",
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-ink-900">{entry.projectName}</p>
                          <p className="text-sm text-ink-500">
                            <span className="font-mono text-xs">{entry.projectKey}</span>{" "}
                            · {entry.projectRole.replace(/_/g, " ").toLowerCase()}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="tnum text-sm text-ink-700">
                            {entry.effectiveFrom} → {entry.effectiveTo ?? "present"}
                          </p>
                          {entry.current && (
                            <div className="mt-1">
                              <StatusBadge tone="blue">Current</StatusBadge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "history" && (
            <div>
              {history.isPending && <LoadingState label="Loading history…" />}
              {history.isError && (
                <ErrorState error={history.error} onRetry={() => void history.refetch()} />
              )}
              {history.isSuccess && history.data.length === 0 && (
                <p className="text-sm text-ink-500">This person has not been in a team yet.</p>
              )}
              {history.isSuccess && history.data.length > 0 && (
                <div className="space-y-2">
                  <p className="mb-3 text-sm text-ink-500">
                    Every period, including those that have ended. Nothing here is overwritten when
                    someone moves.
                  </p>
                  {history.data.map((entry) => (
                    <div
                      key={entry.membershipId}
                      className={cn(
                        "rounded-lg border border-ink-200 border-l-[3px] bg-surface px-4 py-3",
                        entry.current ? "border-l-brand-500" : "border-l-ink-200",
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-ink-900">{entry.teamName}</p>
                          <p className="text-sm text-ink-500">
                            {entry.role.replace(/_/g, " ")} · {entry.membershipPercentage}%
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="tnum text-sm text-ink-700">
                            {entry.effectiveFrom} → {entry.effectiveTo ?? "present"}
                          </p>
                          {entry.current && (
                            <div className="mt-1">
                              <StatusBadge tone="blue">Current</StatusBadge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <EmployeeEditDrawer
        employee={editing ? (employee.data ?? null) : null}
        onClose={() => setEditing(false)}
        onSaved={afterChange}
      />
      <EmploymentStatusDrawer
        employee={changingStatus ? (employee.data ?? null) : null}
        onClose={() => setChangingStatus(false)}
        onSaved={afterChange}
      />
      <AssignRolesDrawer
        employee={assigningRoles ? (employee.data ?? null) : null}
        onClose={() => setAssigningRoles(false)}
        onSaved={afterChange}
      />
    </Drawer>
  );
}

function Row({
  label,
  mono,
  children,
}: {
  label: string;
  mono?: boolean;
  children: ReactNode;
}) {
  return (
    <>
      <dt className="text-ink-500">{label}</dt>
      <dd className={cn("text-ink-900", mono && "font-mono text-xs")}>{children}</dd>
    </>
  );
}
